/**
 * Shared pay-period field helpers for Alpine and Vue.
 *
 * SOURCE OF TRUTH (until Vue adopts this module):
 *   app/javascript/components/shared/PayPeriodCustomDatesSelector.vue
 *
 * Maintenance rule:
 * - If pay-period behavior changes in the Vue component, mirror those changes here.
 * - Vue should import from this module instead of duplicating logic.
 */

import moment from 'moment';
import {
  BIWEEKLY_PAY_PERIOD,
  MONTHLY_PAY_PERIOD,
  PAY_PERIOD_DAY_OF_MONTH_OPTIONS,
  PAY_PERIOD_DAY_OF_WEEK_OPTIONS,
  TWICE_PER_MONTH_PAY_PERIOD,
  WEEKLY_PAY_PERIOD,
} from '~/app_constants';
import { validatePayPeriodState } from '~/utils/validate_pay_period_state';

/**
 * UserOrganization pay_period id → type slug (ids normalized via String()).
 */
const PAY_PERIOD_TYPE_BY_ID = {
  [WEEKLY_PAY_PERIOD]: 'weekly',
  [TWICE_PER_MONTH_PAY_PERIOD]: 'twice_per_month',
  [BIWEEKLY_PAY_PERIOD]: 'biweekly',
  [MONTHLY_PAY_PERIOD]: 'monthly',
};

/**
 * Pay period type that also supports PayNone (null).
 */
function payPeriodType(payPeriodId) {
  if (payPeriodId === '' || payPeriodId == null) { return null; }

  return PAY_PERIOD_TYPE_BY_ID[String(payPeriodId)] ?? null;
}

/**
 * Bi-weekly anchor options for the selected week day.
 */
export function biWeeklyAnchors(periodWeekDay) {
  if (periodWeekDay == null) { return []; }

  const fmt = 'MMM D, YYYY';
  const today = moment();
  const monday = today.clone().startOf('isoWeek');
  const first = monday.clone().add(periodWeekDay, 'days');
  const second = first.clone().add(7, 'days');

  return [
    {
      value: first.format('YYYY-MM-DD').split('T')[0],
      key: 'w1',
      text: `Week 1 (${first.format(fmt)})`,
    },
    {
      value: second.format('YYYY-MM-DD').split('T')[0],
      key: 'w2',
      text: `Week 2 (${second.format(fmt)})`,
    },
  ];
}

/**
 * Returns whether current anchor belongs to the week of new anchor.
 */
function isInAnchorRange(newAnchorDate, currentAnchorDate) {
  if (!newAnchorDate || !currentAnchorDate) {
    return false;
  }

  const newAnchorStart = moment.utc(newAnchorDate).startOf('isoWeek');
  const newAnchorEnd = newAnchorStart.clone().add(6, 'days');
  const currentDate = moment.utc(currentAnchorDate);

  return currentDate.isBetween(newAnchorStart, newAnchorEnd, 'day', '[]');
}

/**
 * Resolves an anchor date against current bi-weekly options.
 */
function resolveBiWeeklyAnchorDate(currentAnchorDate, periodWeekDay) {
  const anchors = biWeeklyAnchors(periodWeekDay);

  if (!anchors.length) { return currentAnchorDate ?? null; }

  const valid = currentAnchorDate && anchors.some((a) => a.value === currentAnchorDate);

  if (currentAnchorDate && valid) { return currentAnchorDate; }

  return isInAnchorRange(anchors[0].value, currentAnchorDate) ? anchors[0].value : anchors[1].value;
}

/**
 * Mutates `customPayPeriod.period_anchor_date` when bi-weekly anchors change.
 */
export function ensureBiWeeklyAnchorDefault(customPayPeriod) {
  if (!customPayPeriod) { return; }

  const periodWeekDay = customPayPeriod.period_week_day ?? customPayPeriod.periodWeekDay;
  const resolved = resolveBiWeeklyAnchorDate(customPayPeriod.period_anchor_date, periodWeekDay);

  if (resolved !== customPayPeriod.period_anchor_date) {
    customPayPeriod.period_anchor_date = resolved;
  }
}

/**
 * Bi-weekly anchor values cache key for watchers.
 */
function biWeeklyAnchorValuesKey(payPeriodId, periodWeekDay) {
  if (payPeriodType(payPeriodId) !== 'biweekly') { return ''; }

  return biWeeklyAnchors(periodWeekDay).map((a) => a.value).join('|');
}

/**
 * Mirrors PayPeriodCustomDatesSelector.vue #selectedDaysOfMonth computed.
 */
export function twicePerMonthSelectedDays(customPayPeriod) {
  if (!customPayPeriod) { return []; }

  return [customPayPeriod.period_start_day, customPayPeriod.period_second_start_day]
    .filter((d) => d != null)
    .sort((a, b) => a - b);
}

/**
 * Normalizes twice-per-month selected values into two sorted day numbers.
 */
function applyTwicePerMonthDaysInput(customPayPeriod, idsOrNumbers) {
  const values = Array.isArray(idsOrNumbers) ? idsOrNumbers : (idsOrNumbers == null ? [] : [idsOrNumbers]);
  const selectedDays = values
    .map((v) => Number(v))
    .filter((v) => Number.isInteger(v) && v > 0)
    .sort((a, b) => a - b)
    .slice(0, 2);

  customPayPeriod.period_start_day = selectedDays[0] ?? null;
  customPayPeriod.period_second_start_day = selectedDays[1] ?? null;
}

/**
 * Toggles a day in twice-per-month selection (max 2 days).
 */
export function twicePerMonthToggleDay(customPayPeriod, value) {
  const current = twicePerMonthSelectedDays(customPayPeriod);
  const idx = current.indexOf(value);
  let next;

  if (idx >= 0) {
    next = current.filter((d) => d !== value);
  } else if (current.length >= 2) {
    return;
  } else {
    next = [...current, value].sort((a, b) => a - b);
  }

  applyTwicePerMonthDaysInput(customPayPeriod, next);
}

/**
 * Clears the twice-per-month selected days.
 */
export function twicePerMonthClear(customPayPeriod) {
  applyTwicePerMonthDaysInput(customPayPeriod, []);
}

/**
 * Human label for monthly/twice-per-month day values.
 */
export function payPeriodDayOfMonthLabel(value) {
  if (value == null) { return ''; }

  const opt = PAY_PERIOD_DAY_OF_MONTH_OPTIONS.find((o) => o.value === value);

  if (!opt) { return ''; }

  return value === 31 ? 'Last day of month' : opt.text;
}

/**
 * Shared custom pay-period validation wrapper.
 * Keeps shape aligned with validatePayPeriodState input contract.
 */
export function validateCustomPayPeriod(payPeriod, customPayPeriod = {}) {
  return validatePayPeriodState({
    payPeriodId: payPeriod == null ? '' : String(payPeriod),
    periodWeekDay: customPayPeriod.period_week_day ?? customPayPeriod.periodWeekDay,
    periodAnchorDate: customPayPeriod.period_anchor_date,
    periodStartDay: customPayPeriod.period_start_day,
    periodSecondStartDay: customPayPeriod.period_second_start_day,
  });
}

/**
 * Helpers spread onto Alpine x-data scopes (payroll setup wizard, batch modal).
 */
export const payPeriodFieldHelpers = {
  get payPeriodDayOfWeekOptions() {
    return PAY_PERIOD_DAY_OF_WEEK_OPTIONS;
  },

  get payPeriodDayOfMonthOptions() {
    return PAY_PERIOD_DAY_OF_MONTH_OPTIONS;
  },

  biWeeklyAnchors,
  biWeeklyAnchorValuesKey,
  isInAnchorRange,
  resolveBiWeeklyAnchorDate,
  ensureBiWeeklyAnchorDefault,
  twicePerMonthSelectedDays,
  twicePerMonthToggleDay,
  twicePerMonthClear,
  payPeriodDayOfMonthLabel,
};
